import React from 'react';
import { Link } from 'react-router-dom';

const CTASection = () => {
  const benefits = [
    {
      icon: '🚀',
      title: 'Get Started in Minutes',
      description: 'Quick setup process with guided onboarding'
    },
    {
      icon: '🛡️',
      title: 'Enterprise Security',
      description: 'Military-grade encryption and AI protection'
    },
    {
      icon: '📱',
      title: 'Mobile & Desktop',
      description: 'Access your assets from any device'
    },
    {
      icon: '💬',
      title: '24/7 Support',
      description: 'Dedicated customer success team'
    }
  ];


  return (
    <section className="py-20 lg:py-32 bg-deepspace relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-deepspace via-deepspace-100 to-deepspace-200"></div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyber-blue/10 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyber-gold/10 rounded-full blur-3xl animate-pulse-slow" style={{animationDelay: '2s'}}></div>
      </div>

      {/* Main CTA Section */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero CTA */}
        <div className="text-center mb-16 lg:mb-20">
          <div className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-cyber-green/10 to-cyber-blue/10 border border-cyber-green/30 rounded-full backdrop-blur-sm mb-6" data-aos="fade-down">
            <div className="w-2 h-2 bg-cyber-green rounded-full animate-pulse mr-3"></div>
            <span className="text-cyber-green text-xs sm:text-sm font-semibold">🚀 Ready to Get Started?</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-text-primary mb-6" data-aos="fade-up" data-aos-delay="100">
            Secure Your Digital Assets
            <span className="block bg-gradient-to-r from-cyber-green to-cyber-blue bg-clip-text text-transparent">
              Today
            </span>
          </h2>
          
          <p className="text-lg sm:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed mb-8" data-aos="fade-up" data-aos-delay="200">
            Join thousands of users who trust AureumVault to protect their crypto investments. 
            Start your free trial today and experience enterprise-grade security.
          </p>

          {/* Main CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center mb-12" data-aos="fade-up" data-aos-delay="300">
            <Link 
              to="/auth"
              className="group relative px-8 sm:px-12 py-4 sm:py-5 bg-gradient-to-r from-cyber-green to-cyber-blue text-white font-bold text-lg sm:text-xl rounded-xl overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-cyber-green/25 inline-block text-center"
            >
              <span className="relative z-10">Connect Wallet</span>
              <div className="absolute inset-0 bg-gradient-to-r from-cyber-blue to-cyber-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </Link>
            
            <Link 
              to="/auth"
              className="group px-8 sm:px-12 py-4 sm:py-5 border-2 border-cyber-green/50 text-cyber-green font-bold text-lg sm:text-xl rounded-xl backdrop-blur-sm transition-all duration-300 hover:bg-cyber-green/10 hover:border-cyber-green hover:scale-105 inline-block text-center"
            >
              Create Wallet
            </Link>
          </div>

          {/* Benefits */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-8 max-w-4xl mx-auto">
            {benefits.map((benefit, index) => (
              <div 
                key={index} 
                className="bg-dark-card/50 backdrop-blur-sm border border-dark-border/50 rounded-xl p-4 lg:p-6 text-center hover:border-cyber-green/50 hover:bg-dark-card/70 transition-all duration-300"
                data-aos="flip-left"
                data-aos-delay={index * 100 + 400}
              >
                <div className="text-3xl lg:text-4xl mb-3">{benefit.icon}</div>
                <h3 className="font-bold text-text-primary mb-2 text-sm lg:text-base">{benefit.title}</h3>
                <p className="text-xs lg:text-sm text-text-muted">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center" data-aos="zoom-in" data-aos-delay="200">
          <div className="bg-gradient-to-r from-cyber-blue/10 via-cyber-gold/10 to-cyber-green/10 backdrop-blur-sm border border-cyber-blue/30 rounded-3xl p-8 lg:p-12">
            <div className="flex items-center justify-center mb-6">
              <img 
                src="/images/all/hexagonal-grid-background.png" 
                alt="Hexagonal Security Grid"
                className="w-20 h-20 lg:w-24 lg:h-24 object-contain filter brightness-0 invert opacity-60"
              />
            </div>
            
            <h3 className="text-2xl lg:text-3xl font-bold text-text-primary mb-4">
              Don't Wait - Your Assets Are at Risk
            </h3>
            <p className="text-text-secondary mb-8 max-w-2xl mx-auto">
              Every day without proper security is a day of vulnerability. Join thousands of users who have already secured their digital assets with AureumVault.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/auth" className="px-8 py-4 bg-gradient-to-r from-cyber-blue to-cyber-gold text-white font-bold rounded-xl hover:scale-105 transition-transform duration-300 inline-block text-center">
                Connect Wallet
              </Link>
              <Link 
                to="/auth"
                className="px-8 py-4 border border-cyber-blue/50 text-cyber-blue font-bold rounded-xl backdrop-blur-sm hover:bg-cyber-blue/10 transition-all duration-300 inline-block text-center"
              >
                Create Wallet
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
